import { Component, ElementRef, HostBinding, Input, OnDestroy, OnInit, Optional, Self } from "@angular/core";
import { MatFormFieldControl } from "@angular/material/form-field";
import { AbstractControl, ControlValueAccessor, NgControl } from "@angular/forms";
import { Subject } from "rxjs";
import { FocusMonitor } from "@angular/cdk/a11y";

export interface FieldContract<T> extends MatFormFieldControl<T>, ControlValueAccessor {
}

@Component({
  template: ''
})
export abstract class FieldComponent<T> implements FieldContract<T>, OnInit, OnDestroy {
  /**
   * @see https://github.com/Microsoft/TypeScript/issues/3841#issuecomment-337560146
   */
  ['constructor']: typeof FieldComponent<T>

  formControl: AbstractControl

  static nextId: number = 0;

  @HostBinding()
  id: string = `${this.getTagName()}-${(this as FieldComponent<T>).constructor.nextId++}`;
  protected _value: T | null = null;
  protected _placeholder: string = ''
  protected _required: boolean;
  protected _disabled: boolean = false
  protected _shouldLabelFloat: boolean = false;
  protected _empty: boolean = false;
  protected _autofilled: boolean;
  protected _userAriaDescribedBy: string;
  protected _controlType: string;

  protected _focused: boolean;

  protected _errorState: boolean;

  stateChanges: Subject<void> = new Subject<void>();

  onChange = (value) => {
  }

  onTouched = () => {
  }

  @Input()
  set value (value) {
    this._value = value
    this.stateChanges.next()
  }

  get value () {
    return this._value
  }

  @Input()
  set placeholder (value: string) {
    this._placeholder = value
    this.stateChanges.next()
  }

  get placeholder (): string {
    return this._placeholder
  }

  @Input()
  set disabled (value: boolean) {
    this._disabled = value
    this.stateChanges.next()
  }

  get disabled (): boolean {
    return this._disabled
  }

  @Input()
  set required (value: boolean) {
    this._required = value
    this.stateChanges.next()
  }

  get required (): boolean {
    return this._required
  }


  @Input()
  set shouldLabelFloat (value: boolean) {
    this._shouldLabelFloat = value
    this.stateChanges.next()
  }

  get shouldLabelFloat (): boolean {
    return this._shouldLabelFloat
  }

  @Input()
  set empty (value: boolean) {
    this._empty = value
    this.stateChanges.next()
  }

  get empty (): boolean {
    return this._empty
  }

  @Input()
  set autofilled (value: boolean) {
    this._autofilled = value
    this.stateChanges.next()
  }

  get autofilled (): boolean {
    return this._autofilled
  }

  @Input()
  set userAriaDescribedBy (value: string) {
    this._userAriaDescribedBy = value
    this.stateChanges.next()
  }

  get userAriaDescribedBy (): string {
    return this._userAriaDescribedBy
  }

  @Input()
  set controlType (value: string) {
    this._controlType = value
    this.stateChanges.next()
  }

  get controlType (): string {
    return this._controlType
  }

  @Input()
  set errorState (value: boolean) {
    this._errorState = value
    this.stateChanges.next()
  }

  get errorState (): boolean {
    return this._errorState
  }

  @Input()
  set focused (value: boolean) {
    this._focused = value
    this.stateChanges.next()
  }

  protected constructor (
    @Optional() @Self() public readonly ngControl: NgControl,
    protected readonly elementRef: ElementRef,
    protected readonly focusMonitor: FocusMonitor
  ) {
    this.initialize()
  }

  ngOnInit () {
  }

  ngOnDestroy (): void {
    this.stateChanges.complete()
  }

  onContainerClick (event: MouseEvent): void {
  }

  setDescribedByIds (ids: string[]): void {
  }

  registerOnChange (fn: any): void {
    this.onChange = fn
  }

  registerOnTouched (fn: any): void {
    this.onTouched = fn
  }

  writeValue (value): void {
    this.value = value
  }

  setDisabledState (isDisabled: boolean) {
    this.disabled = isDisabled
  }

  protected getTagName () {
    return this.elementRef.nativeElement.tagName.toLowerCase();
  }

  protected initialize (): void {
    // Replace the provider from above with this.
    if (this.ngControl != null) {
      // Setting the value accessor directly (instead of using the providers) to avoid running into a circular import.
      this.ngControl.valueAccessor = this;
    }
  }
}
